import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="17863" className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium uppercase tracking-wider text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div className={cn("fixed inset-0 z-40 bg-background/95 backdrop-blur-xl transition-all duration-300 lg:hidden", isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none")}>
      <div className="flex h-full flex-col p-6 pt-24">
        {loading ? <div className="flex flex-col gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-8 w-32 bg-muted/20 animate-pulse rounded-md" data-index={i} />)}
          </div> : <nav className="flex flex-col gap-6">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-bold uppercase tracking-tight text-foreground hover:text-primary" onClick={onClose} data-index={i}>
                {item.label}
              </Link>)}
          </nav>}
        
        <div className="mt-auto border-t border-border pt-8">
          <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-widest font-bold h-12" asChild>
            <Link to="tel:513-822-5882">
              <Phone className="mr-2 h-4 w-4" />
              Get a Cash Offer
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = "https://www.googletagmanager.com/gtag/js?id=AW-18072302523";
    document.head.appendChild(script1);
    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-18072302523');
    `;
    document.head.appendChild(script2);
    return () => {
      if (document.head.contains(script1)) document.head.removeChild(script1);
      if (document.head.contains(script2)) document.head.removeChild(script2);
    };
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-lg shadow-black/20" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link to="#" className="flex-shrink-0 z-50 text-xl font-bold uppercase tracking-tighter">
          CashNest
        </Link>

        <MenuProvider menu_id="49" className="hidden lg:block">
          <DesktopMenu />
        </MenuProvider>

        <div className="hidden lg:flex items-center">
          <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold uppercase tracking-wider transition-all duration-300 shadow-[0_0_10px] shadow-primary/10 hover:shadow-[0_0_20px] hover:shadow-primary/30" asChild>
            <Link to="tel:513-822-5882">
              <Phone className="mr-2 h-4 w-4" />
              Get a Cash Offer
            </Link>
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="lg:hidden z-50 text-foreground hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
        </Button>

        <MenuProvider menu_id="49" className="lg:hidden">
          <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}